<script setup>
// Live preview of a WhatsApp template as the CUSTOMER sees it (an incoming bubble).
// Variables are substituted with the admin's examples; a variable without an example shows as an
// amber {{n}} chip. Built as Vue nodes only — never v-html on admin text.
// Plan: docs/smarticks/whatsapp-template-builder-plan.md (UI spec).
import { computed } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  header: { type: Object, default: () => ({ format: 'NONE' }) },
  body: { type: String, default: '' },
  bodyExamples: { type: Object, default: () => ({}) },
  footer: { type: String, default: '' },
  buttons: { type: Array, default: () => [] },
  emptyBody: { type: String, default: '' },
  sampleName: { type: String, default: '' },
});

const VARIABLE = /(\{\{\d+\}\})/;

const segments = (text, examples) =>
  String(text || '')
    .split(VARIABLE)
    .filter(part => part !== '')
    .map(part => {
      const match = part.match(/^\{\{(\d+)\}\}$/);
      if (!match) return { kind: 'text', value: part };
      const example = String(examples[match[1]] || '').trim();
      return example
        ? { kind: 'value', value: example }
        : { kind: 'token', value: part };
    });

const headerSegments = computed(() =>
  segments(props.header?.text, { 1: props.header?.example })
);
const bodySegments = computed(() => segments(props.body, props.bodyExamples));
const format = computed(() => props.header?.format || 'NONE');

const BUTTON_ICONS = {
  QUICK_REPLY: 'i-lucide-reply',
  URL: 'i-lucide-external-link',
  PHONE_NUMBER: 'i-lucide-phone',
  COPY_CODE: 'i-lucide-copy',
};
</script>

<template>
  <div
    class="rounded-2xl outline outline-1 -outline-offset-1 outline-n-weak bg-n-iris-3 p-4 flex flex-col gap-2"
  >
    <slot name="caption" />
    <div
      class="self-start w-full max-w-[18rem] rounded-xl bg-n-solid-1 shadow-sm overflow-hidden"
    >
      <template v-if="['IMAGE', 'VIDEO'].includes(format)">
        <img
          v-if="format === 'IMAGE' && header.sample_url"
          :src="header.sample_url"
          alt=""
          class="aspect-[1.91/1] w-full object-cover"
        />
        <div
          v-else
          class="aspect-[1.91/1] w-full bg-n-slate-3 flex items-center justify-center"
        >
          <Icon
            :icon="format === 'VIDEO' ? 'i-lucide-play' : 'i-lucide-image'"
            class="size-8 text-n-slate-10"
          />
        </div>
      </template>
      <div
        v-else-if="format === 'DOCUMENT'"
        class="flex items-center gap-2 px-3 py-2 bg-n-slate-3"
      >
        <Icon icon="i-lucide-file-text" class="size-5 text-n-slate-11" />
        <bdi class="text-xs text-n-slate-12 truncate font-inter">{{
          sampleName || 'document.pdf'
        }}</bdi>
      </div>
      <p
        v-else-if="format === 'TEXT' && header.text"
        dir="auto"
        class="px-3 pt-2 text-sm font-semibold text-n-slate-12"
      >
        <template v-for="(part, index) in headerSegments" :key="index">
          <bdi
            v-if="part.kind === 'value'"
            class="rounded-sm bg-n-iris-3 text-n-iris-12 px-0.5"
            >{{ part.value }}</bdi
          >
          <bdi
            v-else-if="part.kind === 'token'"
            dir="ltr"
            class="font-mono text-xs rounded-sm bg-n-amber-3 text-n-amber-11 px-1"
            >{{ part.value }}</bdi
          >
          <template v-else>{{ part.value }}</template>
        </template>
      </p>

      <p
        dir="auto"
        class="px-3 py-2 text-sm text-n-slate-12 whitespace-pre-wrap break-words"
      >
        <template v-if="body">
          <template v-for="(part, index) in bodySegments" :key="index">
            <bdi
              v-if="part.kind === 'value'"
              class="rounded-sm bg-n-iris-3 text-n-iris-12 px-0.5"
              >{{ part.value }}</bdi
            >
            <bdi
              v-else-if="part.kind === 'token'"
              dir="ltr"
              class="font-mono text-xs rounded-sm bg-n-amber-3 text-n-amber-11 px-1"
              >{{ part.value }}</bdi
            >
            <template v-else>{{ part.value }}</template>
          </template>
        </template>
        <span v-else class="text-n-slate-10">{{ emptyBody }}</span>
      </p>

      <p v-if="footer" dir="auto" class="px-3 pb-2 text-xs text-n-slate-11">
        {{ footer }}
      </p>

      <div
        v-for="(button, index) in buttons"
        :key="index"
        class="flex items-center justify-center gap-2 py-2 border-t border-n-weak text-sm font-medium text-n-iris-11"
      >
        <Icon
          :icon="BUTTON_ICONS[button.type] || 'i-lucide-square'"
          class="size-4"
          :class="{ 'rtl:scale-x-[-1]': button.type === 'QUICK_REPLY' }"
        />
        <span dir="auto">{{ button.label }}</span>
      </div>
    </div>
  </div>
</template>
